import capitalize from 'lodash/capitalize';
import partition from 'lodash/partition';

import {reporter} from 'common/report/error-reporting';

import {DataDefinitionsClient} from './data-definitions-client';

const EXTRA_PROPERTY_DEFINITIONS = [
  {
    name: `$date`,
    displayName: `Date`,
    description: `The date and time this event occurred at. All events have this property.`,
    type: `datetime`,
    resourceType: `event`,
  },
  {
    name: `$dataset`,
    displayName: `Project Name`,
    description: `Project Name is a virtual property that you can use to breakdown or filter your data based on the Project ID in which this event/property is tracked. [More about Rollup Projects](https://mixpanel.app.box.com/v/rollup-projects-beta)`,
    type: `string`,
    resourceType: `event`,
  },
  {
    name: `$mp_first_event_time`,
    displayName: `First Seen`,
    description: `First Seen is a datetime profile property that captures the timestamp of the very first event for this user/group. This can be used to filter/segment reports by users who were "new" in a particular timeframe. [More about First Seen](https://help.mixpanel.com/hc/en-us/articles/360001355506-Tracking-First-Time-Users#introducing-first-event-time)`,
    type: `datetime`,
    resourceType: `user`,
  },
];

class PropertyDefinitionsClient extends DataDefinitionsClient {
  buildEndpoint({projectId, workspaceId}) {
    return `${super.buildEndpoint({projectId, workspaceId})}/properties`;
  }

  /**
   * If category is defined, event is not, and vice-versa.
   *
   * Category has two possible values:
   *   user: grab user properties
   *   event: grab event properties
   *   super: grab properties present on the majority of events
   */
  buildQueryParams(
    options: {
      id?: number;
      name?: string;
      category?: string;
      event?: string;
      includeCustom?: boolean;
      useQueryCache?: boolean;
    } = {},
  ) {
    const {id, name, category, event, includeCustom = false, useQueryCache = null} = options;
    // "events" is a common mistake
    // the API is case sensitive, but our frontend javascript feeds in lowercase values
    const resourceType = category === `events` ? `Event` : capitalize(category) || undefined;
    return {
      id,
      name,
      resourceType,
      event,
      includeCustom,
      ...(useQueryCache !== null ? {use_query_cache: useQueryCache} : {}),
    };
  }

  buildRequestBody({propertyDefinition}) {
    return JSON.stringify(propertyDefinition);
  }

  processResponse(response, params) {
    const result = super.processResponse(response, params);
    // the API is case sensitive, but our frontend javascript expects lowercase values
    // TODO: fix the inconsistency with `events` and `event` on the param.category
    // use `user` for now
    const resourceType = params.category === `user` ? `user` : `event`;
    if (Array.isArray(result)) {
      // GOOT for projects with bad user properties see: https://mixpanel.slack.com/archives/C07KZ4NBB/p1605105643087200
      const [valid, invalid] = partition(result, (i) => i.name);
      if (invalid.length > 0) {
        reporter.error(`unexpected property-definitions for ${resourceType}`, invalid);
      }
      return [...valid.map((def) => ({...def, resourceType})), ...EXTRA_PROPERTY_DEFINITIONS];
    } else {
      return {...result, resourceType: result.resourceType.toLowerCase()};
    }
  }
}

export const propertyDefinitionsClient = new PropertyDefinitionsClient();
